import { useEffect, useMemo, useState } from 'react'
import { Columns3, ExternalLink } from 'lucide-react'

import { Checkbox } from 'components/ui/checkbox'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import routes from 'constants/routes'
import { ColumnPickerSheet } from 'features/Explorer/components/ColumnPickerSheet'
import { FilterBar } from 'features/Explorer/components/FilterBar'
import { slugForGrain } from 'features/Explorer/constants'
import * as explorerService from 'features/Explorer/services/explorerService'
import type { ExplorerColumn, ExplorerFilter, ExplorerGrain, ExplorerGrainKey, ExplorerReport } from 'features/Explorer/types/explorer.types'
import { cn } from 'lib/utils'

/**
 * What the Explorer table draws (MYD-504): a saved report, or a table built
 * here with Explorer's own column picker and filter bar, reused unchanged.
 *
 * Only the query is edited here. Row and column caps and overflow are plain
 * settings, drawn by the inspector's ConfigForm like any other element's.
 *
 * Choosing what to show here never decides who sees which rows: the server
 * re-runs the query as each viewer (explorerElements.js).
 */

/** The config fields this panel owns; the settings form leaves them out. */
export const EXPLORER_QUERY_FIELDS = ['savedReportId', 'grain', 'columns', 'filters', 'sorts', 'groupBy', 'bindToStory'] as const

type Config = Record<string, unknown>

interface ExplorerTableInspectorProps {
	config: Config
	onChange: (config: Config) => void
}

const asList = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : [])

/**
 * A filter as the block stores it. FilterBar holds number bounds as typed
 * text; the block's schema wants numbers, and an empty bound means none.
 */
export const toStoredFilter = (filter: ExplorerFilter): ExplorerFilter => {
	const stored: ExplorerFilter = { key: filter.key }
	if (filter.values?.length) stored.values = filter.values.map(String)
	for (const bound of ['min', 'max'] as const) {
		const raw = filter[bound]
		if (raw === undefined || raw === null || String(raw).trim() === '') continue
		const number = Number(raw)
		if (Number.isFinite(number)) stored[bound] = number
	}
	if (filter.from) stored.from = filter.from
	if (filter.to) stored.to = filter.to
	if (typeof filter.value === 'boolean') stored.value = filter.value
	return stored
}

/** The config without the inline query, for switching to a saved report. */
const withoutInline = (config: Config): Config =>
	Object.fromEntries(Object.entries(config).filter(([key]) => !['grain', 'columns', 'filters', 'sorts', 'groupBy'].includes(key)))

export function ExplorerTableInspector({ config, onChange }: ExplorerTableInspectorProps) {
	const savedReportId = typeof config.savedReportId === 'string' ? config.savedReportId : ''
	const [mode, setMode] = useState<'saved' | 'build'>(savedReportId || !config.grain ? 'saved' : 'build')
	const [reports, setReports] = useState<ExplorerReport[] | null>(null)
	const [grains, setGrains] = useState<ExplorerGrain[]>([])

	useEffect(() => {
		let cancelled = false
		explorerService
			.listReports()
			.then(list => !cancelled && setReports(list))
			.catch(() => !cancelled && setReports([]))
		explorerService
			.getGrains()
			.then(list => !cancelled && setGrains(list.filter(grain => grain.available)))
			.catch(() => undefined)
		return () => {
			cancelled = true
		}
	}, [])

	const grainLabel = (key: string) => grains.find(grain => grain.key === key)?.label ?? key

	return (
		<div className="space-y-3">
			<div role="radiogroup" aria-label="Table source" className="grid grid-cols-2 rounded-md border border-gray-200 p-0.5 text-xs">
				{(
					[
						['saved', 'Saved report'],
						['build', 'Build here'],
					] as const
				).map(([value, label]) => (
					<button
						key={value}
						type="button"
						role="radio"
						aria-checked={mode === value}
						onClick={() => {
							setMode(value)
							// Building drops the saved report, which would otherwise win.
							if (value === 'build' && savedReportId) onChange(Object.fromEntries(Object.entries(config).filter(([key]) => key !== 'savedReportId')))
						}}
						className={cn('rounded px-2 py-1.5 font-medium', mode === value ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-50')}
					>
						{label}
					</button>
				))}
			</div>

			{mode === 'saved' ? (
				<SavedReportField reports={reports} value={savedReportId} grainLabel={grainLabel} onChange={id => onChange({ ...withoutInline(config), savedReportId: id || undefined })} />
			) : (
				<InlineQuery config={config} grains={grains} onChange={onChange} />
			)}

			<BindToStoryField checked={config.bindToStory === true} onChange={bindToStory => onChange({ ...config, bindToStory: bindToStory || undefined })} />
		</div>
	)
}

/**
 * Narrow the table to the story's asset (MYD-603), so one table follows each
 * story it is copied into. Applies to a saved report as well as one built here.
 * Which pairs work is the server's to say: the block explains a mismatch.
 */
function BindToStoryField({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
	return (
		<div className="flex items-start gap-2 rounded-md border border-gray-200 p-2.5">
			<Checkbox id="explorer-bind-to-story" checked={checked} onCheckedChange={onChange} className="mt-0.5" aria-labelledby="explorer-bind-to-story-label" />
			<div className="space-y-0.5">
				<button type="button" id="explorer-bind-to-story-label" onClick={() => onChange(!checked)} className="text-left text-xs font-medium text-gray-800">
					Limit to this story's asset
				</button>
				<p className="text-[11px] text-muted-foreground">
					Shows only the rows for the project, location, portfolio or evaluation this story is about, so the same table works in every story.
				</p>
			</div>
		</div>
	)
}

// =============================================================================
// Saved report
// =============================================================================

function SavedReportField({
	reports,
	value,
	grainLabel,
	onChange,
}: {
	reports: ExplorerReport[] | null
	value: string
	grainLabel: (key: string) => string
	onChange: (id: string) => void
}) {
	const chosen = reports?.find(report => report.id === value) ?? null
	const byGrain = useMemo(() => {
		const groups = new Map<string, ExplorerReport[]>()
		for (const report of reports ?? []) groups.set(report.grain, [...(groups.get(report.grain) ?? []), report])
		return [...groups]
	}, [reports])

	if (reports === null) return <p className="text-xs text-muted-foreground">Loading your reports...</p>
	if (reports.length === 0) {
		return (
			<p className="text-xs text-muted-foreground">
				You have no saved Explorer reports yet. Save one in{' '}
				<a href={routes.EXPLORER} target="_blank" rel="noreferrer" className="text-primary hover:underline">
					Explorer
				</a>
				, or build a table here.
			</p>
		)
	}

	return (
		<div className="space-y-2">
			<div className="space-y-1.5">
				<Label htmlFor="explorer-report" className="text-xs font-medium text-gray-600">
					Report
				</Label>
				<Select id="explorer-report" value={value} onChange={event => onChange(event.target.value)}>
					<option value="">Choose a report</option>
					{byGrain.map(([grain, list]) => (
						<optgroup key={grain} label={grainLabel(grain)}>
							{list.map(report => (
								<option key={report.id} value={report.id}>
									{report.name}
									{report.isOwner ? '' : ' (shared with you)'}
								</option>
							))}
						</optgroup>
					))}
				</Select>
			</div>
			{/* A saved id that is not in the list: deleted, or no longer shared. */}
			{value && !chosen && <p className="text-[11px] text-amber-700">This report is no longer available to you.</p>}
			{chosen && (
				<a
					href={`${routes.EXPLORER}/${slugForGrain(chosen.grain)}?report=${encodeURIComponent(chosen.id)}`}
					target="_blank"
					rel="noreferrer"
					className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
				>
					<ExternalLink className="h-3 w-3" /> Open in Explorer
				</a>
			)}
			<p className="text-[11px] text-muted-foreground">Edits to the report in Explorer show here the next time it loads. Each viewer sees only the rows they can access.</p>
		</div>
	)
}

// =============================================================================
// Built here
// =============================================================================

function InlineQuery({ config, grains, onChange }: { config: Config; grains: ExplorerGrain[]; onChange: (config: Config) => void }) {
	const grain = (typeof config.grain === 'string' ? config.grain : '') as ExplorerGrainKey | ''
	const columns = asList<string>(config.columns)
	const filters = asList<ExplorerFilter>(config.filters)
	const sort = asList<{ key: string; direction: 'asc' | 'desc' }>(config.sorts)[0] ?? null
	const groupBy = typeof config.groupBy === 'string' ? config.groupBy : ''

	const [catalog, setCatalog] = useState<ExplorerColumn[]>([])
	const [picking, setPicking] = useState(false)

	useEffect(() => {
		if (!grain) return setCatalog([])
		let cancelled = false
		explorerService
			.getColumns(grain)
			.then(list => !cancelled && setCatalog(list))
			.catch(() => !cancelled && setCatalog([]))
		return () => {
			cancelled = true
		}
	}, [grain])

	const chosen = columns.map(key => catalog.find(column => column.key === key)).filter((column): column is ExplorerColumn => Boolean(column))
	const update = (patch: Config) => onChange({ ...config, ...patch })

	/** New columns: drop a sort or grouping on one no longer chosen, as Explorer does. */
	const setColumns = (next: string[]) => {
		const patch: Config = { columns: next }
		if (sort && !next.includes(sort.key)) patch.sorts = []
		if (groupBy && !next.includes(groupBy)) patch.groupBy = undefined
		update(patch)
	}

	return (
		<div className="space-y-3">
			<div className="space-y-1.5">
				<Label htmlFor="explorer-grain" className="text-xs font-medium text-gray-600">
					Rows
				</Label>
				<Select
					id="explorer-grain"
					value={grain}
					onChange={event => {
						const next = grains.find(candidate => candidate.key === event.target.value)
						// A new row type starts from its default layout: nothing else carries over.
						onChange({
							...withoutInline(config),
							grain: next?.key,
							columns: next?.defaultColumns ?? [],
							sorts: next?.defaultSort ?? [],
						})
					}}
				>
					<option value="">Choose what each row is</option>
					{grains.map(candidate => (
						<option key={candidate.key} value={candidate.key}>
							{candidate.label}
						</option>
					))}
				</Select>
			</div>

			{grain && (
				<>
					<button
						type="button"
						onClick={() => setPicking(true)}
						className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50"
					>
						<Columns3 className="h-3.5 w-3.5" /> Columns ({columns.length})
					</button>

					<div className="space-y-1.5">
						<p className="text-xs font-medium text-gray-600">Filters</p>
						<FilterBar catalog={catalog} filters={filters} grain={grain} onChange={next => update({ filters: next.map(toStoredFilter) })} />
					</div>

					<div className="grid grid-cols-[1fr_auto] gap-2">
						<div className="space-y-1.5">
							<Label htmlFor="explorer-sort" className="text-xs font-medium text-gray-600">
								Sort by
							</Label>
							<Select
								id="explorer-sort"
								value={sort?.key ?? ''}
								onChange={event => update({ sorts: event.target.value ? [{ key: event.target.value, direction: sort?.direction ?? 'asc' }] : [] })}
							>
								<option value="">Default</option>
								{chosen
									.filter(column => column.sortable)
									.map(column => (
										<option key={column.key} value={column.key}>
											{column.label}
										</option>
									))}
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="explorer-direction" className="text-xs font-medium text-gray-600">
								Order
							</Label>
							<Select
								id="explorer-direction"
								value={sort?.direction ?? 'asc'}
								disabled={!sort}
								onChange={event => sort && update({ sorts: [{ key: sort.key, direction: event.target.value as 'asc' | 'desc' }] })}
							>
								<option value="asc">Ascending</option>
								<option value="desc">Descending</option>
							</Select>
						</div>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="explorer-group" className="text-xs font-medium text-gray-600">
							Group by
						</Label>
						<Select id="explorer-group" value={groupBy} onChange={event => update({ groupBy: event.target.value || undefined })}>
							<option value="">No grouping</option>
							{chosen
								.filter(column => column.groupable)
								.map(column => (
									<option key={column.key} value={column.key}>
										{column.label}
									</option>
								))}
						</Select>
					</div>

					<a href={`${routes.EXPLORER}/${slugForGrain(grain)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
						<ExternalLink className="h-3 w-3" /> Open Explorer
					</a>

					<ColumnPickerSheet open={picking} onClose={() => setPicking(false)} catalog={catalog} selected={columns} onChange={setColumns} />
				</>
			)}
		</div>
	)
}

export default ExplorerTableInspector
